import React, { HTMLAttributes, PropsWithChildren } from "react";
import { CloseIcon } from "../icons";

export type Action = {
  label: string;
  appearance: "primary" | "secondary";
} & HTMLAttributes<HTMLButtonElement>;

type ContextBarActionProps = Action;

const ContextBarAction: React.FC<ContextBarActionProps> = ({
  label,
  onClick,
  appearance,
  ...props
}) => {
  // MIGRATE: WOMBAT
  const primaryClasses =
    "inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-white transition duration-150 ease-in-out bg-primary-600 border border-transparent rounded-md hover:bg-primary-500 focus:outline-none focus:shadow-outline-primary focus:border-primary-700 active:bg-primary-700  disabled:opacity-75 disabled:cursor-not-allowed";
  const secondaryClasses =
    "inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 transition duration-150 ease-in-out disabled:opacity-75 disabled:cursor-not-allowed";

  return (
    <span className="inline-flex rounded-md shadow-sm">
      <button
        type="button"
        onClick={onClick}
        className={appearance === "primary" ? primaryClasses : secondaryClasses}
        {...props}
      >
        {label}
      </button>
    </span>
  );
};

type ContextBarProps = {
  title?: string;
  description?: string;
  actions?: Action[];
};

const ContextBar: React.FC<PropsWithChildren<ContextBarProps>> = ({
  actions,
  title,
  description,
  ...props
}) => {
  return (
    <div className="w-full max-w-md">
      <div className="flex flex-col bg-white shadow-xl min-h-layout">
        <div className="flex-1 h-0">
          {title && (
            <header className="px-4 py-6 space-y-1 bg-primary-700 sm:px-6">
              <div className="flex items-center justify-between space-x-3">
                <h2 className="text-lg font-medium leading-7 text-white">
                  {title}
                </h2>
                <div className="flex items-center h-7">
                  <button
                    aria-label="Close panel"
                    className="text-primary-200 transition duration-150 ease-in-out hover:text-white"
                  >
                    <CloseIcon iconClassName="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-sm leading-5 text-primary-300">
                  {description}
                </p>
              </div>
            </header>
          )}
          <div className="flex flex-col justify-between flex-1">
            {props.children}
          </div>
        </div>
        <div className="flex justify-end flex-shrink-0 px-4 py-4 space-x-4">
          {actions?.map((action) => (
            <ContextBarAction {...action} key={action.label} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ContextBar);
